import { NewLearningRequestStep } from "utils/types";
import { ProcurementRoute } from "utils/graphql/Global";
import { logger } from "utils/logger";
import portalSettingsManager from "utils/portalSettingsManager";
import useSubmitBudgetSignal from "./useSubmitBudgetSignal";

/**
 * Returns a `navigate` function that routes to BudgetCheck when the feature is
 * enabled, or silently sends a skipped budget signal and goes straight to
 * `skipToStep` (CommissioningDetails by default) when it is disabled.
 *
 * Usage:
 *   const navigateWithBudgetCheck = useBudgetCheckNavigation();
 *   navigateWithBudgetCheck(onNext);   // or onPrevious – any step-navigation fn
 *   navigateWithBudgetCheck(onPrevious, NewLearningRequestStep.NlrAdditionalDetails); // e.g. from ProductRecommendations
 */
export const useBudgetCheckNavigation = () => {
  const submitBudget = useSubmitBudgetSignal();

  const navigate = (
    stepFn: (step: NewLearningRequestStep) => void,
    skipToStep: NewLearningRequestStep = NewLearningRequestStep.CommissioningDetails,
  ) => {
    const isBudgetCheckEnabled =
      portalSettingsManager.application?.features?.learningRequestBudgetCheck
        ?.IsBudgetCheckEnabled ?? true; // default to enabled

    if (isBudgetCheckEnabled) {
      stepFn(NewLearningRequestStep.BudgetCheck);
    } else {
      submitBudget(
        {
          isUnderBudget: false,
          procurementRoute: ProcurementRoute.Unknown,
        },
        () => {
          logger.info("Budget signal sent: Skipped due to disabled budget check");
          stepFn(skipToStep);
        },
      );
    }
  };

  return navigate;
};

export default useBudgetCheckNavigation;
